import React, { useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import { Link } from 'react-router-dom';
import { useTheme } from '../../../contexts/ThemeContext';
import { useNotification } from '../../../contexts/NotificationContext';
import { cn } from '../../../lib/utils';
import { Button, Label, TextInput, NumberInput, SearchIcon, Switch, Select, Textarea, Badge, useConfirm } from '../../../components/ui';
import { useCustomSettings, CustomSettingType } from './useData';
import { SIDEBAR_LAYOUT_OPTIONS, SidebarLayoutMode } from '../../../config/sidebarLayout';
import { fetcher } from '../../../services/fetcher';
import api from '../../../services/api';
import { useApplicationSettings } from './useData';

const MAX_NATIONALITIES = 6;

type NationalityUsageRow = {
  id: number;
  code: string | null;
  name: string;
  sortOrder: number;
  crewCount: number;
};

type NationalityUsageResponse = {
  rows: NationalityUsageRow[];
  summary: { catalog: number; inPool: number; crewWithNationality: number };
};

type SettingsModule = 'general' | 'email' | 'custom' | 'finance' | 'purchasing' | 'pms' | 'hr' | 'ai';

const MODULES: Array<{ id: SettingsModule; label: string; description: string }> = [
  { id: 'general', label: 'General', description: 'App name, layout, features' },
  { id: 'email', label: 'Email', description: 'SMTP for outbound mail' },
  { id: 'custom', label: 'Custom', description: 'Variables added at runtime' },
  { id: 'finance', label: 'Finance', description: 'Accounting and journals' },
  { id: 'purchasing', label: 'Purchasing', description: 'RFQ and supply links' },
  { id: 'pms', label: 'PMS', description: 'Maintenance and work orders' },
  { id: 'hr', label: 'HR', description: 'Crew and rank requirements' },
  { id: 'ai', label: 'AI', description: 'OpenAI and document scan' },
];

const CUSTOM_TYPE_OPTIONS: Array<{ value: CustomSettingType; label: string }> = [
  { value: 'string', label: 'Text' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Yes / No' },
  { value: 'secret', label: 'Secret' },
  { value: 'json', label: 'JSON' },
];

const DENSITY_OPTIONS = [
  { value: 'compact', label: 'Compact' },
  { value: 'normal', label: 'Normal' },
  { value: 'comfortable', label: 'Comfortable' },
] as const;

const selectClass = (colors: ReturnType<typeof useTheme>['colors']) =>
  cn(
    'mt-1.5 w-full rounded-xl border px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-current/20',
    colors.border,
    colors.surface,
    colors.text,
  );

const EmptyModulePanel: React.FC<{ title: string; hint: string }> = ({ title, hint }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div className="flex min-h-[320px] flex-1 items-center justify-center px-4">
      <div className={cn('w-full max-w-lg rounded-2xl border border-dashed px-10 py-16 text-center', colors.border)}>
        <div
          className={cn(
            'mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl',
            colors.textMuted,
            isDark ? 'bg-white/[0.06]' : colors.surfaceHover,
          )}
        >
          <svg className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
        </div>
        <p className={cn('text-base font-semibold', colors.text)}>{title}</p>
        <p className={cn('mx-auto mt-2 max-w-sm text-sm leading-relaxed', colors.textMuted)}>{hint}</p>
      </div>
    </div>
  );
};

const SettingsCard: React.FC<{
  title: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  footer?: React.ReactNode;
}> = ({ title, description, children, className, footer }) => {
  const { colors } = useTheme();
  return (
    <section className={cn('flex flex-col overflow-hidden rounded-2xl border', colors.border, colors.surface, className)}>
      <div className={cn('border-b px-5 py-4', colors.border)}>
        <h3 className={cn('text-sm font-semibold', colors.text)}>{title}</h3>
        {description && <div className={cn('mt-1 text-sm leading-relaxed', colors.textMuted)}>{description}</div>}
      </div>
      <div className="flex flex-1 flex-col gap-4 px-5 py-5">{children}</div>
      {footer && <div className={cn('mt-auto border-t px-5 py-3', colors.border)}>{footer}</div>}
    </section>
  );
};

const RequirementNationalitiesPicker: React.FC<{
  rows: NationalityUsageRow[];
  summary?: NationalityUsageResponse['summary'];
  selectedIds: number[];
  onChange: (next: number[]) => void;
  loading?: boolean;
}> = ({ rows, summary, selectedIds, onChange, loading }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const [query, setQuery] = useState('');
  const [poolOnly, setPoolOnly] = useState(true);

  const byId = useMemo(() => new Map(rows.map((r) => [r.id, r])), [rows]);

  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows;
    if (poolOnly) list = list.filter((r) => r.crewCount > 0 || selectedSet.has(r.id));
    if (q) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          (r.code && r.code.toLowerCase().includes(q)),
      );
    }
    return [...list].sort((a, b) => {
      const aSel = selectedSet.has(a.id) ? 0 : 1;
      const bSel = selectedSet.has(b.id) ? 0 : 1;
      if (aSel !== bSel) return aSel - bSel;
      if (selectedSet.has(a.id) && selectedSet.has(b.id)) {
        return selectedIds.indexOf(a.id) - selectedIds.indexOf(b.id);
      }
      if (b.crewCount !== a.crewCount) return b.crewCount - a.crewCount;
      return a.name.localeCompare(b.name);
    });
  }, [rows, poolOnly, query, selectedSet, selectedIds]);

  const toggle = (id: number) => {
    if (selectedSet.has(id)) {
      onChange(selectedIds.filter((x) => x !== id));
      return;
    }
    if (selectedIds.length >= MAX_NATIONALITIES) return;
    onChange([...selectedIds, id]);
  };

  const moveSelected = (id: number, dir: -1 | 1) => {
    const idx = selectedIds.indexOf(id);
    if (idx < 0) return;
    const next = idx + dir;
    if (next < 0 || next >= selectedIds.length) return;
    const copy = [...selectedIds];
    const [item] = copy.splice(idx, 1);
    copy.splice(next, 0, item);
    onChange(copy);
  };

  return (
    <div className="space-y-4">
      <div>
        <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
          <p className={cn('text-sm font-medium', colors.text)}>
            Selected for Document Requirements ({selectedIds.length}/{MAX_NATIONALITIES})
          </p>
          {summary ? (
            <p className={cn('text-xs', colors.textMuted)}>
              {summary.inPool} in crew pool · {summary.crewWithNationality} crew with nationality
            </p>
          ) : null}
        </div>
        {selectedIds.length === 0 ? (
          <p
            className={cn(
              'rounded-xl border border-dashed px-4 py-3 text-sm',
              colors.border,
              colors.textMuted,
            )}
          >
            Pick up to {MAX_NATIONALITIES} nationalities below. Order here is the column order on Rank
            requirements.
          </p>
        ) : (
          <ol className="flex flex-wrap gap-2">
            {selectedIds.map((id, index) => {
              const row = byId.get(id);
              return (
                <li
                  key={id}
                  className={cn(
                    'inline-flex items-center gap-1.5 rounded-xl border px-2.5 py-1.5 text-sm',
                    colors.border,
                    isDark ? 'bg-white/[0.05]' : 'bg-black/[0.03]',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-5 w-5 items-center justify-center rounded-md text-[11px] font-semibold',
                      isDark ? 'bg-cyan-900/70 text-cyan-100' : 'bg-cyan-100 text-cyan-900',
                    )}
                  >
                    {index + 1}
                  </span>
                  <span className={cn('font-medium', colors.text)}>
                    {row?.code || row?.name || `#${id}`}
                  </span>
                  {row?.code && row.name ? (
                    <span className={cn('hidden sm:inline', colors.textMuted)}>{row.name}</span>
                  ) : null}
                  {row && row.crewCount > 0 ? (
                    <span className={cn('text-xs', colors.textMuted)}>{row.crewCount}</span>
                  ) : (
                    <span
                      className={cn(
                        'rounded px-1 text-[10px] font-medium uppercase',
                        isDark ? 'bg-amber-900/50 text-amber-200' : 'bg-amber-100 text-amber-800',
                      )}
                    >
                      Not in pool
                    </span>
                  )}
                  <span className="ml-0.5 inline-flex items-center gap-0.5">
                    <button
                      type="button"
                      title="Move earlier"
                      disabled={index === 0}
                      onClick={() => moveSelected(id, -1)}
                      className={cn(
                        'rounded px-1 text-xs disabled:opacity-30',
                        colors.textMuted,
                        colors.interactiveHover,
                      )}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      title="Move later"
                      disabled={index === selectedIds.length - 1}
                      onClick={() => moveSelected(id, 1)}
                      className={cn(
                        'rounded px-1 text-xs disabled:opacity-30',
                        colors.textMuted,
                        colors.interactiveHover,
                      )}
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      title="Remove"
                      onClick={() => toggle(id)}
                      className={cn(
                        'rounded px-1.5 text-xs font-medium',
                        isDark ? 'text-red-300' : 'text-red-600',
                        colors.interactiveHover,
                      )}
                    >
                      ×
                    </button>
                  </span>
                </li>
              );
            })}
          </ol>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[12rem] flex-1">
          <SearchIcon
            className={cn(
              'pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2',
              colors.textMuted,
            )}
          />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Filter by name or code…"
            className={cn(
              'h-10 w-full rounded-xl border pl-9 pr-3 text-sm outline-none focus:ring-2 focus:ring-indigo-500/25',
              colors.border,
              colors.surface,
              colors.text,
            )}
          />
        </div>
        <label className={cn('inline-flex cursor-pointer items-center gap-2 text-sm', colors.text)}>
          <Switch
            checked={poolOnly}
            onChange={(e) => setPoolOnly(e.target.checked)}
            size="sm"
          />
          In crew pool only
        </label>
      </div>

      <div
        className={cn(
          'max-h-[22rem] overflow-y-auto rounded-xl border dashboard-scrollbar',
          colors.border,
        )}
      >
        {loading ? (
          <p className={cn('px-4 py-8 text-center text-sm', colors.textMuted)}>Loading nationalities…</p>
        ) : filtered.length === 0 ? (
          <p className={cn('px-4 py-8 text-center text-sm', colors.textMuted)}>
            {poolOnly
              ? 'No nationalities found in the crew pool. Turn off “In crew pool only” to browse the catalog.'
              : 'No nationalities match this filter.'}
          </p>
        ) : (
          <ul>
            {filtered.map((row) => {
              const selected = selectedSet.has(row.id);
              const col = selected ? selectedIds.indexOf(row.id) + 1 : null;
              const atCap = !selected && selectedIds.length >= MAX_NATIONALITIES;
              const inPool = row.crewCount > 0;
              return (
                <li key={row.id} className={cn('border-b last:border-b-0', colors.border)}>
                  <button
                    type="button"
                    disabled={atCap}
                    onClick={() => toggle(row.id)}
                    title={atCap ? `Maximum ${MAX_NATIONALITIES} nationalities` : undefined}
                    className={cn(
                      'flex w-full items-center gap-3 px-3 py-2.5 text-left transition disabled:cursor-not-allowed disabled:opacity-45',
                      selected
                        ? isDark
                          ? 'bg-cyan-950/35'
                          : 'bg-cyan-50'
                        : colors.interactiveHover,
                    )}
                  >
                    <span
                      className={cn(
                        'flex h-5 w-5 shrink-0 items-center justify-center rounded border text-[11px] font-bold',
                        selected
                          ? isDark
                            ? 'border-cyan-400 bg-cyan-500 text-slate-950'
                            : 'border-cyan-600 bg-cyan-600 text-white'
                          : colors.border,
                        colors.textMuted,
                      )}
                      aria-hidden
                    >
                      {selected ? '✓' : ''}
                    </span>
                    <span
                      className={cn(
                        'w-12 shrink-0 truncate text-xs font-semibold uppercase tracking-wide',
                        colors.textMuted,
                      )}
                    >
                      {row.code || '—'}
                    </span>
                    <span className={cn('min-w-0 flex-1 truncate text-sm font-medium', colors.text)}>
                      {row.name}
                    </span>
                    {inPool ? (
                      <span
                        className={cn(
                          'shrink-0 rounded-md px-2 py-0.5 text-xs font-medium tabular-nums',
                          isDark ? 'bg-emerald-950/60 text-emerald-200' : 'bg-emerald-50 text-emerald-800',
                        )}
                      >
                        {row.crewCount} in pool
                      </span>
                    ) : (
                      <span
                        className={cn(
                          'shrink-0 rounded-md px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide',
                          isDark ? 'bg-white/5 text-white/45' : 'bg-black/5 text-black/45',
                        )}
                      >
                        Not in pool
                      </span>
                    )}
                    {col != null ? (
                      <span
                        className={cn(
                          'shrink-0 rounded-md px-2 py-0.5 text-xs font-semibold',
                          isDark ? 'bg-cyan-900/70 text-cyan-100' : 'bg-cyan-100 text-cyan-900',
                        )}
                      >
                        Col {col}
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

const FeatureSwitch: React.FC<{
  id: string;
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}> = ({ id, label, hint, checked, onChange }) => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-4 rounded-xl border px-4 py-3.5 transition',
        colors.border,
        checked &&
          cn(
            colors.primaryBorderAll,
            isDark ? 'bg-white/[0.04]' : 'bg-black/[0.03]',
          ),
      )}
    >
      <div className="min-w-0">
        <label htmlFor={id} className={cn('cursor-pointer text-sm font-medium', colors.text)}>
          {label}
        </label>
        {hint && <p className={cn('mt-0.5 text-xs leading-snug', colors.textMuted)}>{hint}</p>}
      </div>
      <Switch
        id={id}
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        size="default"
        variant="pill"
        aria-label={label}
      />
    </div>
  );
};

export const ApplicationSettingsPage: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const { showError, showSuccess } = useNotification();
  const { applicationSettings, mutate: mutateApp } = useApplicationSettings();
  const { customSettings, mutate: mutateCustom } = useCustomSettings();
  const confirm = useConfirm();
  const { data: nationalityUsage, isLoading: loadingNationalities } = useSWR<NationalityUsageResponse>(
    '/crew/nationality-usage',
    fetcher,
    { revalidateOnFocus: false },
  );

  const [module, setModule] = useState<SettingsModule>('general');

  const [appName, setAppName] = useState('Atlas SCMS');
  const [sidebarStyle, setSidebarStyle] = useState<SidebarLayoutMode>('classic');
  const [vesselAppUrl, setVesselAppUrl] = useState('');
  const [apiTimeout, setApiTimeout] = useState<number | null>(60000);
  const [defaultTablePageSize, setDefaultTablePageSize] = useState<number | null>(200);
  const [defaultTableDensity, setDefaultTableDensity] = useState<'compact' | 'normal' | 'comfortable'>('normal');
  const [pageSizeOptionsText, setPageSizeOptionsText] = useState('50, 100, 200, 500');
  const [enableAdvancedSearch, setEnableAdvancedSearch] = useState(true);
  const [enableExport, setEnableExport] = useState(true);
  const [enableDarkMode, setEnableDarkMode] = useState(true);
  const [enableTablePresets, setEnableTablePresets] = useState(true);
  const [enableConditionalFormatting, setEnableConditionalFormatting] = useState(true);
  const [enableFooter, setEnableFooter] = useState(true);
  const [savingGeneral, setSavingGeneral] = useState(false);

  const [publicRfqUrl, setPublicRfqUrl] = useState('');
  const [savingPurchasing, setSavingPurchasing] = useState(false);

  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [savingCrew, setSavingCrew] = useState(false);

  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState('gpt-4o-mini');
  const [embeddingModel, setEmbeddingModel] = useState('text-embedding-3-small');
  const [visionModel, setVisionModel] = useState('gpt-4o-mini');
  const [visionMaxPages, setVisionMaxPages] = useState<number | null>(25);
  const [savingOpenAi, setSavingOpenAi] = useState(false);

  const [mailEnabled, setMailEnabled] = useState(false);
  const [mailHost, setMailHost] = useState('');
  const [mailPort, setMailPort] = useState<number | null>(587);
  const [mailSecure, setMailSecure] = useState(false);
  const [mailUser, setMailUser] = useState('');
  const [mailPassword, setMailPassword] = useState('');
  const [mailFrom, setMailFrom] = useState('');
  const [savingMail, setSavingMail] = useState(false);

  const [customKey, setCustomKey] = useState('');
  const [customOriginalKey, setCustomOriginalKey] = useState<string | null>(null);
  const [customType, setCustomType] = useState<CustomSettingType>('string');
  const [customDescription, setCustomDescription] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [customActive, setCustomActive] = useState(true);
  const [savingCustom, setSavingCustom] = useState(false);

  const activeModule = MODULES.find((m) => m.id === module) ?? MODULES[0];

  useEffect(() => {
    const ids = applicationSettings?.['crew.requirementNationalityIds'];
    if (Array.isArray(ids)) setSelectedIds(ids.map((id) => Number(id)).filter((n) => Number.isFinite(n)));
  }, [applicationSettings]);

  useEffect(() => {
    if (!applicationSettings) return;
    setAppName(applicationSettings.appName || 'Atlas SCMS');
    setSidebarStyle((applicationSettings.sidebarStyle as SidebarLayoutMode) || 'classic');
    setVesselAppUrl(applicationSettings['general.vesselAppUrl'] || '');
    setApiTimeout(applicationSettings.apiTimeout ?? 60000);
    setDefaultTablePageSize(applicationSettings.defaultTablePageSize ?? 200);
    setDefaultTableDensity(applicationSettings.defaultTableDensity || 'normal');
    setPageSizeOptionsText((applicationSettings.pageSizeOptions ?? [50, 100, 200, 500]).join(', '));
    setEnableAdvancedSearch(applicationSettings.enableAdvancedSearch !== false);
    setEnableExport(applicationSettings.enableExport !== false);
    setEnableDarkMode(applicationSettings.enableDarkMode !== false);
    setEnableTablePresets(applicationSettings.enableTablePresets !== false);
    setEnableConditionalFormatting(applicationSettings.enableConditionalFormatting !== false);
    setEnableFooter(applicationSettings.enableFooter !== false);

    setPublicRfqUrl(applicationSettings['purchasing.publicRfqFrontendBaseUrl'] || '');

    setModel(applicationSettings['openai.model'] || 'gpt-4o-mini');
    setEmbeddingModel(applicationSettings['openai.embeddingModel'] || 'text-embedding-3-small');
    setVisionModel(applicationSettings['openai.visionModel'] || 'gpt-4o-mini');
    setVisionMaxPages(Number(applicationSettings['openai.visionMaxPages']) || 25);
    setApiKey('');

    setMailEnabled(applicationSettings['mail.enabled'] === true);
    setMailHost(applicationSettings['mail.host'] || '');
    setMailPort(applicationSettings['mail.port'] ?? 587);
    setMailSecure(applicationSettings['mail.secure'] === true);
    setMailUser(applicationSettings['mail.user'] || '');
    setMailFrom(applicationSettings['mail.from'] || '');
    setMailPassword('');
  }, [applicationSettings]);

  const onChangeSelection = (next: number[]) => {
    if (next.length > MAX_NATIONALITIES) {
      showError(`Select at most ${MAX_NATIONALITIES} nationalities.`);
      setSelectedIds(next.slice(0, MAX_NATIONALITIES));
      return;
    }
    setSelectedIds(next);
  };

  const onSaveGeneral = async () => {
    if (!appName.trim()) {
      showError('App name is required.');
      return;
    }
    const pageSizeOptions = pageSizeOptionsText
      .split(/[,;\s]+/)
      .map((s) => Number(s.trim()))
      .filter((n) => Number.isFinite(n) && n > 0)
      .map((n) => Math.floor(n));
    if (pageSizeOptions.length < 1) {
      showError('Enter at least one page size option (e.g. 50, 100, 200).');
      return;
    }
    setSavingGeneral(true);
    try {
      await api.put('/settings/general', {
        appName: appName.trim(),
        sidebarStyle,
        vesselAppUrl: vesselAppUrl.trim() || '',
        apiTimeout: Number(apiTimeout),
        defaultTablePageSize: Number(defaultTablePageSize),
        defaultTableDensity,
        pageSizeOptions,
        enableAdvancedSearch,
        enableExport,
        enableDarkMode,
        enableTablePresets,
        enableConditionalFormatting,
        enableFooter,
      });
      showSuccess('General settings saved.');
      await mutateApp();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingGeneral(false);
    }
  };

  const onSavePurchasing = async () => {
    setSavingPurchasing(true);
    try {
      await api.put('/settings/purchasing', {
        publicRfqFrontendBaseUrl: publicRfqUrl.trim() || '',
      });
      showSuccess('Purchasing settings saved.');
      await mutateApp();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingPurchasing(false);
    }
  };

  const onSaveCrew = async () => {
    if (selectedIds.length < 1 || selectedIds.length > MAX_NATIONALITIES) {
      showError(`Select between 1 and ${MAX_NATIONALITIES} nationalities.`);
      return;
    }
    setSavingCrew(true);
    try {
      await api.put('/settings/crew/requirement-nationalities', {
        value: selectedIds.map((id) => Number(id)),
      });
      showSuccess('Crew requirement nationalities saved.');
      await mutateApp();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingCrew(false);
    }
  };

  const onSaveOpenAi = async () => {
    if (!model.trim() || !embeddingModel.trim() || !visionModel.trim()) {
      showError('Model names cannot be empty.');
      return;
    }
    const pages = Number(visionMaxPages);
    if (!Number.isFinite(pages) || pages < 1 || pages > 100) {
      showError('Vision max pages must be between 1 and 100.');
      return;
    }
    setSavingOpenAi(true);
    try {
      await api.put('/settings/openai', {
        ...(apiKey.trim() ? { apiKey: apiKey.trim() } : {}),
        model: model.trim(),
        embeddingModel: embeddingModel.trim(),
        visionModel: visionModel.trim(),
        visionMaxPages: Math.floor(pages),
      });
      showSuccess('OpenAI settings saved.');
      setApiKey('');
      await mutateApp();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingOpenAi(false);
    }
  };

  const onSaveMail = async () => {
    const port = Number(mailPort);
    if (!Number.isFinite(port) || port < 1 || port > 65535) {
      showError('SMTP port must be between 1 and 65535.');
      return;
    }
    setSavingMail(true);
    try {
      await api.put('/settings/mail', {
        enabled: mailEnabled,
        host: mailHost.trim(),
        port: Math.floor(port),
        secure: mailSecure,
        user: mailUser.trim(),
        from: mailFrom.trim(),
        ...(mailPassword.trim() ? { password: mailPassword } : {}),
      });
      showSuccess('Email settings saved.');
      setMailPassword('');
      await mutateApp();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingMail(false);
    }
  };

  const resetCustomForm = () => {
    setCustomKey('');
    setCustomOriginalKey(null);
    setCustomType('string');
    setCustomDescription('');
    setCustomValue('');
    setCustomActive(true);
  };

  const onEditCustom = (row: (typeof customSettings)[number]) => {
    setCustomOriginalKey(row.key);
    setCustomKey(row.key);
    setCustomType(row.valueType);
    setCustomDescription(row.description || '');
    setCustomActive(row.isActive);
    if (row.valueType === 'secret') setCustomValue('');
    else if (row.valueType === 'boolean') setCustomValue(row.value === true ? 'true' : 'false');
    else if (row.valueType === 'json') setCustomValue(row.value == null ? '' : JSON.stringify(row.value, null, 2));
    else setCustomValue(row.value == null ? '' : String(row.value));
  };

  const onSaveCustom = async () => {
    if (!customKey.trim()) {
      showError('Key is required.');
      return;
    }
    let value: unknown = customValue;
    if (customType === 'boolean') value = customValue === 'true';
    if (customType === 'number') value = customValue.trim() === '' ? NaN : Number(customValue);
    setSavingCustom(true);
    try {
      const body = {
        key: customKey.trim(),
        valueType: customType,
        description: customDescription.trim(),
        isActive: customActive,
        value,
      };
      if (customOriginalKey) {
        await api.put(`/settings/custom/${encodeURIComponent(customOriginalKey)}`, body);
      } else {
        await api.post('/settings/custom', body);
      }
      showSuccess(customOriginalKey ? 'Custom variable updated.' : 'Custom variable saved.');
      resetCustomForm();
      await mutateCustom();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Save failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    } finally {
      setSavingCustom(false);
    }
  };

  const onDeleteCustom = async (key: string) => {
    const ok = await confirm({
      title: 'Delete variable',
      message: `Delete ${key}? The application will no longer see this value.`,
      confirmText: 'Delete',
      confirmVariant: 'danger',
    });
    if (!ok) return;
    try {
      await api.delete(`/settings/custom/${encodeURIComponent(key)}`);
      if (customOriginalKey === key) resetCustomForm();
      showSuccess('Custom variable deleted.');
      await mutateCustom();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (e instanceof Error ? e.message : 'Delete failed');
      showError(Array.isArray(msg) ? msg.join(', ') : String(msg));
    }
  };

  const apiKeySet = applicationSettings?.['openai.apiKeySet'] === true;
  const apiKeySource = applicationSettings?.['openai.apiKeySource'] || 'none';
  const rfqSource = applicationSettings?.['purchasing.publicRfqFrontendBaseUrlSource'] || 'none';
  const vesselSource = applicationSettings?.['general.vesselAppUrlSource'] || 'none';
  const mailPasswordSet = applicationSettings?.['mail.passwordSet'] === true;
  const mailPasswordSource = applicationSettings?.['mail.passwordSource'] || 'none';

  return (
    <div className={cn('flex h-full min-h-0 overflow-hidden rounded-2xl border shadow-sm', colors.surface, colors.border)}>
      <aside className={cn('flex w-48 shrink-0 flex-col border-r lg:w-56', colors.border, colors.headerBg)}>
        <div className={cn('border-b px-4 py-3.5', colors.border)}>
          <p className={cn('text-[11px] font-semibold uppercase tracking-wider', colors.textMuted)}>Modules</p>
        </div>
        <nav className="flex flex-1 flex-col gap-1 overflow-y-auto p-2.5" aria-label="Settings modules">
          {MODULES.map((item) => {
            const active = module === item.id;
            return (
              <button
                key={item.id}
                type="button"
                onClick={() => setModule(item.id)}
                className={cn(
                  'rounded-xl px-3.5 py-3 text-left transition',
                  active
                    ? cn(
                        colors.surface,
                        colors.primaryFg,
                        'shadow-sm ring-1',
                        isDark ? 'ring-white/15' : 'ring-black/10',
                      )
                    : cn(colors.textMuted, colors.interactiveHover),
                )}
              >
                <span className={cn('block text-sm font-semibold', active ? '' : colors.text)}>{item.label}</span>
                <span
                  className={cn(
                    'mt-0.5 block text-[11px] leading-snug',
                    active ? 'opacity-75' : colors.textMuted,
                  )}
                >
                  {item.description}
                </span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <div className={cn('flex shrink-0 items-center justify-between gap-4 border-b px-6 py-4', colors.border)}>
          <div>
            <h2 className={cn('text-lg font-semibold tracking-tight', colors.text)}>{activeModule.label}</h2>
            <p className={cn('mt-0.5 text-sm', colors.textMuted)}>{activeModule.description}</p>
          </div>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto">
          <div className="px-6 py-6">
            {module === 'general' && (
              <div className="space-y-5">
                <div className="grid gap-5 xl:grid-cols-2">
                  <SettingsCard title="Branding & links" description="Shown across the office app and vessel redirects.">
                    <div>
                      <Label htmlFor="app-name" required>
                        App name
                      </Label>
                      <TextInput
                        id="app-name"
                        value={appName}
                        onChange={(e) => setAppName(e.target.value)}
                        showClearButton={false}
                        helperText="Top bar and footer"
                      />
                    </div>
                    <div>
                      <Label htmlFor="vessel-url">Vessel app URL</Label>
                      <TextInput
                        id="vessel-url"
                        value={vesselAppUrl}
                        onChange={(e) => setVesselAppUrl(e.target.value)}
                        placeholder="http://192.168.1.88:3001"
                        showClearButton={false}
                        helperText={
                          vesselSource === 'none'
                            ? 'Optional redirect target for vessel accounts'
                            : `From ${vesselSource} · blank clears override`
                        }
                      />
                    </div>
                  </SettingsCard>

                  <SettingsCard title="Tables & API" description="Organization defaults. Users can still override density and page size.">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="api-timeout" required>
                          API timeout (ms)
                        </Label>
                        <NumberInput
                          id="api-timeout"
                          value={apiTimeout ?? undefined}
                          onChange={(e) => setApiTimeout(e.target.value)}
                          min={1000}
                          max={600000}
                          allowDecimals={false}
                          showClearButton={false}
                        />
                      </div>
                      <div>
                        <Label htmlFor="default-page-size" required>
                          Default page size
                        </Label>
                        <NumberInput
                          id="default-page-size"
                          value={defaultTablePageSize ?? undefined}
                          onChange={(e) => setDefaultTablePageSize(e.target.value)}
                          min={1}
                          max={2000}
                          allowDecimals={false}
                          showClearButton={false}
                        />
                      </div>
                      <div>
                        <Label htmlFor="table-density" required>
                          Default density
                        </Label>
                        <select
                          id="table-density"
                          value={defaultTableDensity}
                          onChange={(e) =>
                            setDefaultTableDensity(e.target.value as 'compact' | 'normal' | 'comfortable')
                          }
                          className={selectClass(colors)}
                        >
                          {DENSITY_OPTIONS.map((opt) => (
                            <option key={opt.value} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="page-size-options" required>
                          Page size options
                        </Label>
                        <TextInput
                          id="page-size-options"
                          value={pageSizeOptionsText}
                          onChange={(e) => setPageSizeOptionsText(e.target.value)}
                          placeholder="50, 100, 200, 500"
                          showClearButton={false}
                          helperText="Comma-separated"
                        />
                      </div>
                    </div>
                  </SettingsCard>
                </div>

                <SettingsCard title="Default sidebar layout" description="Organization default. Users can pick a personal layout in Account settings.">
                  <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-5">
                    {SIDEBAR_LAYOUT_OPTIONS.map((opt) => {
                      const active = sidebarStyle === opt.id;
                      return (
                        <button
                          key={opt.id}
                          type="button"
                          onClick={() => setSidebarStyle(opt.id)}
                          className={cn(
                            'rounded-xl border px-3.5 py-3 text-left transition',
                            active
                              ? cn(
                                  colors.primaryBorderAll,
                                  'ring-1 shadow-sm',
                                  isDark ? 'bg-white/[0.04] ring-white/15' : 'bg-black/[0.03] ring-black/10',
                                )
                              : cn(colors.border, colors.interactiveHover),
                          )}
                        >
                          <p className={cn('text-sm font-semibold', active ? colors.primaryFg : colors.text)}>
                            {opt.label}
                          </p>
                          <p className={cn('mt-1 text-[11px] leading-snug', colors.textMuted)}>{opt.description}</p>
                        </button>
                      );
                    })}
                  </div>
                </SettingsCard>

                <SettingsCard title="Features" description="Turn product capabilities on or off for everyone.">
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    <FeatureSwitch
                      id="flag-search"
                      label="Advanced search"
                      hint="Show advanced filters on lists"
                      checked={enableAdvancedSearch}
                      onChange={setEnableAdvancedSearch}
                    />
                    <FeatureSwitch
                      id="flag-export"
                      label="Export"
                      hint="Allow exporting list data"
                      checked={enableExport}
                      onChange={setEnableExport}
                    />
                    <FeatureSwitch
                      id="flag-dark"
                      label="Dark mode"
                      hint="Theme switcher in the top bar"
                      checked={enableDarkMode}
                      onChange={setEnableDarkMode}
                    />
                    <FeatureSwitch
                      id="flag-presets"
                      label="Table presets"
                      hint="Save and apply table layouts"
                      checked={enableTablePresets}
                      onChange={setEnableTablePresets}
                    />
                    <FeatureSwitch
                      id="flag-formatting"
                      label="Conditional formatting"
                      hint="Highlight rules on tables"
                      checked={enableConditionalFormatting}
                      onChange={setEnableConditionalFormatting}
                    />
                    <FeatureSwitch
                      id="flag-footer"
                      label="Footer"
                      hint="Show the application footer"
                      checked={enableFooter}
                      onChange={setEnableFooter}
                    />
                  </div>
                </SettingsCard>

                <div className="flex justify-end">
                  <Button type="button" loading={savingGeneral} onClick={onSaveGeneral}>
                    Save general
                  </Button>
                </div>
              </div>
            )}

            {module === 'email' && (
              <div className="mx-auto max-w-3xl space-y-5">
                <SettingsCard
                  title="Outbound email"
                  description="SMTP settings used when the system sends mail. A blank password keeps the current one."
                  footer={
                    <div className="flex justify-end">
                      <Button type="button" loading={savingMail} onClick={onSaveMail}>
                        Save email
                      </Button>
                    </div>
                  }
                >
                  <FeatureSwitch
                    id="mail-enabled"
                    label="Email enabled"
                    hint="Master switch for outbound mail"
                    checked={mailEnabled}
                    onChange={setMailEnabled}
                  />
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="sm:col-span-2">
                      <Label htmlFor="mail-host">SMTP host</Label>
                      <TextInput
                        id="mail-host"
                        value={mailHost}
                        onChange={(e) => setMailHost(e.target.value)}
                        placeholder="smtp.example.com"
                        showClearButton={false}
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-port" required>
                        Port
                      </Label>
                      <NumberInput
                        id="mail-port"
                        value={mailPort ?? undefined}
                        onChange={(e) => setMailPort(e.target.value)}
                        min={1}
                        max={65535}
                        allowDecimals={false}
                        showClearButton={false}
                      />
                    </div>
                    <div className="flex items-end">
                      <FeatureSwitch
                        id="mail-secure"
                        label="Use TLS"
                        hint="On for port 465"
                        checked={mailSecure}
                        onChange={setMailSecure}
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-user">Username</Label>
                      <TextInput
                        id="mail-user"
                        value={mailUser}
                        onChange={(e) => setMailUser(e.target.value)}
                        showClearButton={false}
                        autoComplete="off"
                      />
                    </div>
                    <div>
                      <Label htmlFor="mail-password">Password</Label>
                      <TextInput
                        id="mail-password"
                        type="password"
                        value={mailPassword}
                        onChange={(e) => setMailPassword(e.target.value)}
                        placeholder={mailPasswordSet ? 'Leave blank to keep current password' : ''}
                        showClearButton={false}
                        autoComplete="new-password"
                        helperText={
                          mailPasswordSet
                            ? `Configured (${mailPasswordSource === 'database' ? 'settings' : 'environment'})`
                            : 'No password configured yet'
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="mail-from">From address</Label>
                      <TextInput
                        id="mail-from"
                        value={mailFrom}
                        onChange={(e) => setMailFrom(e.target.value)}
                        placeholder="Atlas SCMS <noreply@example.com>"
                        showClearButton={false}
                      />
                    </div>
                  </div>
                </SettingsCard>
              </div>
            )}

            {module === 'custom' && (
              <div className="space-y-5">
                <SettingsCard
                  title={customOriginalKey ? `Edit ${customOriginalKey}` : 'New variable'}
                  description="Stored in application settings immediately. No code change or rebuild."
                  footer={
                    <div className="flex justify-end gap-2">
                      {customOriginalKey && (
                        <Button type="button" variant="secondary" onClick={resetCustomForm}>
                          Cancel
                        </Button>
                      )}
                      <Button type="button" loading={savingCustom} onClick={onSaveCustom}>
                        {customOriginalKey ? 'Update variable' : 'Add variable'}
                      </Button>
                    </div>
                  }
                >
                  <div className="grid gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="custom-key" required>
                        Key
                      </Label>
                      <TextInput
                        id="custom-key"
                        value={customKey}
                        onChange={(e) => setCustomKey(e.target.value)}
                        placeholder="company.shortName"
                        showClearButton={false}
                        helperText="Letters, numbers, dots, underscores, hyphens"
                      />
                    </div>
                    <div>
                      <Label htmlFor="custom-type" required>
                        Type
                      </Label>
                      <Select
                        id="custom-type"
                        value={customType}
                        options={CUSTOM_TYPE_OPTIONS}
                        showClearButton={false}
                        onChange={(e) => setCustomType(e.target.value as CustomSettingType)}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="custom-description">Description</Label>
                      <TextInput
                        id="custom-description"
                        value={customDescription}
                        onChange={(e) => setCustomDescription(e.target.value)}
                        showClearButton={false}
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="custom-value">Value</Label>
                      {customType === 'boolean' ? (
                        <Select
                          id="custom-value"
                          value={customValue === 'true' ? 'true' : 'false'}
                          options={[
                            { value: 'true', label: 'Yes' },
                            { value: 'false', label: 'No' },
                          ]}
                          showClearButton={false}
                          onChange={(e) => setCustomValue(e.target.value)}
                        />
                      ) : customType === 'json' ? (
                        <Textarea
                          id="custom-value"
                          value={customValue}
                          onChange={(e) => setCustomValue(e.target.value)}
                          rows={5}
                          placeholder='{"example":true}'
                        />
                      ) : (
                        <TextInput
                          id="custom-value"
                          type={customType === 'secret' ? 'password' : customType === 'number' ? 'number' : 'text'}
                          value={customValue}
                          onChange={(e) => setCustomValue(e.target.value)}
                          placeholder={customType === 'secret' && customOriginalKey ? 'Leave blank to keep current secret' : ''}
                          showClearButton={false}
                          autoComplete="off"
                        />
                      )}
                    </div>
                    <FeatureSwitch
                      id="custom-active"
                      label="Active"
                      hint="Inactive variables stay stored but are marked off"
                      checked={customActive}
                      onChange={setCustomActive}
                    />
                  </div>
                </SettingsCard>

                <SettingsCard title="Saved variables" description="These keys are read from the database at runtime.">
                  {customSettings.length === 0 ? (
                    <p className={cn('text-sm', colors.textMuted)}>No custom variables yet.</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-left text-sm">
                        <thead>
                          <tr className={cn('border-b text-xs uppercase tracking-wide', colors.border, colors.textMuted)}>
                            <th className="px-2 py-2 font-medium">Key</th>
                            <th className="px-2 py-2 font-medium">Value</th>
                            <th className="px-2 py-2 font-medium">Type</th>
                            <th className="px-2 py-2 font-medium">Status</th>
                            <th className="px-2 py-2 font-medium" />
                          </tr>
                        </thead>
                        <tbody>
                          {customSettings.map((row) => (
                            <tr key={row.key} className={cn('border-b', colors.border)}>
                              <td className={cn('px-2 py-2 align-top', colors.text)}>
                                <div className="font-medium">{row.key}</div>
                                {row.description ? (
                                  <div className={cn('mt-0.5 text-xs', colors.textMuted)}>{row.description}</div>
                                ) : null}
                              </td>
                              <td className={cn('max-w-[240px] truncate px-2 py-2 align-top', colors.text)}>
                                {row.valueType === 'secret'
                                  ? row.secretSet
                                    ? '••••••••'
                                    : '—'
                                  : row.valueType === 'json'
                                    ? JSON.stringify(row.value)
                                    : String(row.value ?? '—')}
                              </td>
                              <td className="px-2 py-2 align-top">{row.valueType}</td>
                              <td className="px-2 py-2 align-top">
                                <Badge variant={row.isActive ? 'success' : 'secondary'}>
                                  {row.isActive ? 'Active' : 'Inactive'}
                                </Badge>
                              </td>
                              <td className="px-2 py-2 align-top text-right">
                                <div className="flex justify-end gap-2">
                                  <Button type="button" variant="secondary" size="sm" onClick={() => onEditCustom(row)}>
                                    Edit
                                  </Button>
                                  <Button type="button" variant="secondary" size="sm" onClick={() => onDeleteCustom(row.key)}>
                                    Delete
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SettingsCard>
              </div>
            )}

            {module === 'finance' && (
              <EmptyModulePanel title="No Finance settings yet" hint="Accounting and journal defaults will appear here." />
            )}

            {module === 'purchasing' && (
              <div className="mx-auto max-w-3xl">
                <SettingsCard
                  title="Public RFQ links"
                  description="Base URL used when staff copy a supplier RFQ link. Overrides PUBLIC_RFQ_FRONTEND_BASE_URL."
                  footer={
                    <div className="flex justify-end">
                      <Button type="button" loading={savingPurchasing} onClick={onSavePurchasing}>
                        Save purchasing
                      </Button>
                    </div>
                  }
                >
                  <div>
                    <Label htmlFor="rfq-base-url">Public RFQ frontend base URL</Label>
                    <TextInput
                      id="rfq-base-url"
                      value={publicRfqUrl}
                      onChange={(e) => setPublicRfqUrl(e.target.value)}
                      placeholder="http://192.168.1.88:3000"
                      showClearButton={false}
                      helperText={
                        rfqSource === 'none'
                          ? 'Example: https://office.example.com — no trailing slash'
                          : `Currently from ${rfqSource}. Clear and save to fall back to .env.`
                      }
                    />
                  </div>
                </SettingsCard>
              </div>
            )}

            {module === 'pms' && (
              <EmptyModulePanel title="No PMS settings yet" hint="Maintenance and work-order defaults will appear here." />
            )}

            {module === 'hr' && (
              <div className="mx-auto max-w-3xl">
                <SettingsCard
                  title="Requirement nationalities"
                  description={
                    <>
                      Columns on{' '}
                      <Link to="/parameters/crew?section=certificate-requirements" className={cn(colors.primaryFg, 'hover:underline')}>
                        Document Requirements
                      </Link>
                      . Prefer nationalities already used in the{' '}
                      <Link to="/crew" className={cn(colors.primaryFg, 'hover:underline')}>
                        Crew Pool
                      </Link>
                      . Select 1–{MAX_NATIONALITIES}; order is column order.
                    </>
                  }
                  footer={
                    <div className="flex justify-end">
                      <Button type="button" loading={savingCrew} onClick={onSaveCrew}>
                        Save
                      </Button>
                    </div>
                  }
                >
                  <RequirementNationalitiesPicker
                    rows={nationalityUsage?.rows ?? []}
                    summary={nationalityUsage?.summary}
                    selectedIds={selectedIds}
                    onChange={onChangeSelection}
                    loading={loadingNationalities}
                  />
                </SettingsCard>
              </div>
            )}

            {module === 'ai' && (
              <div className="space-y-5">
                <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)]">
                  <SettingsCard title="API key" description="Required for invoice AI scan. Leave blank to keep the current key.">
                    <TextInput
                      id="openai-api-key"
                      type="password"
                      autoComplete="new-password"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder={apiKeySet ? 'Leave blank to keep current key' : 'sk-…'}
                      showClearButton={false}
                      helperText={
                        apiKeySet
                          ? `Configured (${apiKeySource === 'database' ? 'settings' : 'environment'})`
                          : 'No key configured yet'
                      }
                    />
                  </SettingsCard>

                  <SettingsCard title="Models" description="Overrides .env model names for the running API.">
                    <div className="grid gap-4 sm:grid-cols-2">
                      <div>
                        <Label htmlFor="openai-model" required>
                          Model
                        </Label>
                        <TextInput
                          id="openai-model"
                          value={model}
                          onChange={(e) => setModel(e.target.value)}
                          placeholder="gpt-4o-mini"
                          showClearButton={false}
                          helperText="OPENAI_MODEL"
                        />
                      </div>
                      <div>
                        <Label htmlFor="openai-embedding" required>
                          Embedding model
                        </Label>
                        <TextInput
                          id="openai-embedding"
                          value={embeddingModel}
                          onChange={(e) => setEmbeddingModel(e.target.value)}
                          placeholder="text-embedding-3-small"
                          showClearButton={false}
                          helperText="OPENAI_EMBEDDING_MODEL"
                        />
                      </div>
                      <div>
                        <Label htmlFor="openai-vision" required>
                          Vision model
                        </Label>
                        <TextInput
                          id="openai-vision"
                          value={visionModel}
                          onChange={(e) => setVisionModel(e.target.value)}
                          placeholder="gpt-4o-mini"
                          showClearButton={false}
                          helperText="OPENAI_VISION_MODEL"
                        />
                      </div>
                      <div>
                        <Label htmlFor="openai-vision-pages" required>
                          Vision max pages
                        </Label>
                        <NumberInput
                          id="openai-vision-pages"
                          value={visionMaxPages ?? undefined}
                          onChange={(e) => setVisionMaxPages(e.target.value)}
                          min={1}
                          max={100}
                          allowDecimals={false}
                          showClearButton={false}
                          helperText="1–100"
                        />
                      </div>
                    </div>
                  </SettingsCard>
                </div>

                <div className="flex justify-end">
                  <Button type="button" loading={savingOpenAi} onClick={onSaveOpenAi}>
                    Save OpenAI
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationSettingsPage;
